import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  AdaptiveDialog,
  Alert,
  Button,
  DateInput,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  GlassTable,
  Input,
  StatusPill,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import type {
  BankAccountOption,
  FilingCalendarFailureReason,
  FilingObligationSummary,
  PaymentSourceAccountOption,
  PayrollActionFailureReason,
  PayRunPostActionsController,
  StatutoryPaymentRecord,
  StatutoryPaymentType,
} from '../../data/wagepro_store';
import type { PayRun } from '../../data/types';
import { format_date, format_money } from '../../lib/format';
import {
  build_statutory_payment_rows,
  can_generate_disbursement_file,
  can_record_statutory_payment,
  decode_base64_to_blob,
  is_valid_iso_date,
  map_wagepro_locale_to_payslip_locale,
  parse_filing_report_id,
  validate_disbursement_input,
  type StatutoryPaymentRow,
} from './post_run_actions_calculations';
import {
  findPaymentSourceAccountByQuickFillValue,
  resolvePaymentSourceQuickFillValue,
} from './post_run_payment_source_picker';
import {
  build_bank_account_options,
  build_payment_source_quick_fill_options,
  build_payslip_employee_options,
} from './post_run_select_options';
import {
  classify_disbursement_error_message,
  type DisbursementErrorClass,
} from './post_run_disbursement_error_classification';
import {
  classify_statutory_error_message,
  type StatutoryActionErrorClass,
} from './post_run_statutory_error_classification';
import { restartSignIn } from '../../lib/auth/step_up.js';
import { triggerDownload } from '../../lib/download.js';

/**
 * Sub-sections composing `PostRunStep` (`wizard_steps.tsx`, C4c #21956).
 * Split into its own file rather than growing `wizard_steps.tsx` past
 * readability — `PostRunStep` itself stays a thin composition root there.
 * Business logic (validation, row-shaping, base64 decoding) lives in
 * `post_run_actions_calculations.ts`; this file holds only JSX + event
 * wiring. The shared browser download side effect lives in `lib/download.ts`
 * (`triggerDownload`, imported above) — used here and by the Payroll Reports
 * screen (#23307).
 */

const DISBURSEMENT_BANKS = ['sampath', 'combank', 'hnb', 'boc'] as const;
type DisbursementBankId = (typeof DISBURSEMENT_BANKS)[number];
/**
 * Sentinel `value` for "no bank chosen yet" -- `StyledSelect<V extends string>`
 * has no null value slot, and any value matching no option renders the
 * `placeholder`. Never emitted by `onChange` (no option carries it), so the
 * mapping back to `null` there is type plumbing, not a runtime guard.
 */
const NO_BANK_VALUE = '';
const BANK_NAME_KEY: Record<DisbursementBankId, string> = {
  sampath: 'pay_run.post_run.bank_sampath',
  combank: 'pay_run.post_run.bank_combank',
  hnb: 'pay_run.post_run.bank_hnb',
  boc: 'pay_run.post_run.bank_boc',
};

const STATUTORY_TYPE_KEY: Record<StatutoryPaymentType, string> = {
  epf_employee: 'pay_run.post_run.statutory_type_epf_employee',
  epf_employer: 'pay_run.post_run.statutory_type_epf_employer',
  etf: 'pay_run.post_run.statutory_type_etf',
  paye: 'pay_run.post_run.statutory_type_paye',
};

/**
 * Maps `classify_disbursement_error_message`'s result to a translation key —
 * never rendered as the raw server message (adversarial-review finding, C8
 * second round, #22410). Kept here (not in the pure classification module)
 * since only this component has a `t()` to resolve the key against.
 */
const DISBURSEMENT_ERROR_KEY: Record<DisbursementErrorClass, string> = {
  not_approved: 'pay_run.post_run.disbursement_error_not_approved',
  no_lines: 'pay_run.post_run.disbursement_error_no_lines',
  no_bank_details: 'pay_run.post_run.disbursement_error_no_bank_details',
  not_found: 'pay_run.post_run.disbursement_error_not_found',
  unknown: 'pay_run.post_run.disbursement_error_generic',
};

/**
 * Maps `classify_statutory_error_message`'s result to a translation key --
 * never rendered as the raw server message (adversarial-review finding, C9
 * second round, #22415). Shared by both `StatutoryRecordDialog.handleSubmit`
 * and `StatutorySection.handleVoidConfirm` below, since both call sites
 * classify the same message shape from the same classifier.
 */
const STATUTORY_ERROR_KEY: Record<StatutoryActionErrorClass, string> = {
  not_found: 'pay_run.post_run.statutory_error_not_found',
  not_approved: 'pay_run.post_run.statutory_error_not_approved',
  duplicate: 'pay_run.post_run.statutory_error_duplicate',
  amount_mismatch: 'pay_run.post_run.statutory_error_amount_mismatch',
  locked_period: 'pay_run.post_run.statutory_error_locked_period',
  already_voided: 'pay_run.post_run.statutory_error_already_voided',
  void_unsupported: 'pay_run.post_run.statutory_error_void_unsupported',
  reconciled: 'pay_run.post_run.statutory_error_reconciled',
  step_up_required: 'pay_run.post_run.statutory_error_step_up_required',
  value_cap_exceeded: 'pay_run.post_run.statutory_error_value_cap_exceeded',
  audience_required: 'pay_run.post_run.statutory_error_audience_required',
  bank_account_invalid: 'pay_run.post_run.statutory_error_bank_account_invalid',
  unknown: 'pay_run.post_run.statutory_error_generic',
};

export function PayslipSection({
  run,
  post_actions,
}: {
  run: PayRun;
  post_actions: PayRunPostActionsController;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [employeeId, setEmployeeId] = useState(
    run.line_items[0]?.employee_id ?? '',
  );
  const [isDownloading, setIsDownloading] = useState(false);
  /**
   * `'entitlement_required'` (the entity is missing the `payroll` add-on —
   * `oauth_pay_run_post_actions.ts`'s `isPayrollAddonRequiredError`) gets its
   * own actionable message, never the generic `'error'` copy — showing "buy
   * the add-on" copy for an unrelated failure (or vice versa) would be
   * actively misleading (C8, #22330).
   */
  const [errorReason, setErrorReason] = useState<
    'entitlement_required' | 'error' | null
  >(null);

  async function handleDownload(): Promise<void> {
    if (employeeId === '') return;
    setErrorReason(null);
    setIsDownloading(true);
    try {
      const result = await post_actions.generate_payslip({
        pay_run_id: run.id,
        employee_id: employeeId,
        locale: map_wagepro_locale_to_payslip_locale(locale),
      });
      if (result.ok) {
        // decode_base64_to_blob (-> atob) throws on a malformed payload --
        // caught below rather than left to escape as an unhandled rejection
        // (adversarial review finding: this was the one throw path in the
        // whole feature not already funneled through the { ok: false }
        // contract every dev resolver guarantees).
        await triggerDownload(
          decode_base64_to_blob(result.pdf_base64, 'application/pdf'),
          result.filename,
        );
      } else {
        // The real server detail (`result.message` on the `'error'` branch)
        // is logged for debugging, never shown to the user verbatim -- the
        // Alert below always renders a translated generic
        // `payslip_error`/`payslip_entitlement_required` copy. Matches the
        // "log the real detail, show a translated generic message"
        // convention used elsewhere in this codebase (adversarial-review
        // finding, C8 second round, #22410 -- the previous version of this
        // type dropped `message` entirely, so there was nothing to log
        // here).
        if (result.reason === 'error') {
          console.error(
            '[wagepro-web] generate payslip failed',
            result.message,
          );
        }
        setErrorReason(result.reason);
      }
    } catch (err) {
      console.error('[wagepro-web] payslip download failed', err);
      setErrorReason('error');
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="wp-post-run-section">
      <h3 className="font-semibold mb-2">{t('pay_run.generate_payslips')}</h3>
      {run.line_items.length === 0 ? (
        <p className="nv-help">{t('pay_run.post_run.payslip_no_employees')}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {/* No visible label in this inline row, so `ariaLabel` names the
              trigger for screen readers. The wrapper fixes the width — the
              StyledSelect trigger is `w-full` by default, and unconstrained
              it would push the download button onto its own line. */}
          <div className="w-full sm:w-[280px]">
            <StyledSelect
              ariaLabel={t('pay_run.post_run.payslip_employee_label')}
              value={employeeId}
              options={build_payslip_employee_options(run.line_items)}
              onChange={(next) => {
                setEmployeeId(next);
              }}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={isDownloading}
            onClick={() => {
              void handleDownload();
            }}
          >
            {isDownloading
              ? t('pay_run.post_run.payslip_downloading')
              : t('pay_run.post_run.download_payslip')}
          </Button>
        </div>
      )}
      {errorReason === 'entitlement_required' && (
        <Alert
          variant="danger"
          title={t('pay_run.post_run.payslip_entitlement_required')}
        />
      )}
      {errorReason === 'error' && (
        <Alert variant="danger" title={t('pay_run.post_run.payslip_error')} />
      )}
    </div>
  );
}

export function DisbursementSection({
  run,
  post_actions,
}: {
  run: PayRun;
  post_actions: PayRunPostActionsController;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [open, setOpen] = useState(false);
  const [bankId, setBankId] = useState<DisbursementBankId | null>(null);
  const [accountNo, setAccountNo] = useState('');
  const [valueDate, setValueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  /**
   * The "quick-fill" payment-source-account picker (C8, #22330) — sourced
   * from C5's narrow `payroll.listPaymentSourceAccounts` (#22324), never
   * the general `account.list` (deliberately not OAuth-allowlisted for a
   * payroll token — C5's own design decision, see
   * `PaymentSourceAccountOption`'s doc comment on `wagepro_store.tsx`).
   * `accountNo` (the `Input` below) is the ONLY thing actually submitted as
   * `debit_account_no` and stays fully editable at all times — this list
   * only offers a one-click convenience that fills `accountNo` from a
   * known account's `code`/`id` (see
   * `post_run_payment_source_picker.ts`'s module docblock for why an
   * EARLIER version of this picker that replaced the free-text field
   * outright was an adversarial-review finding: a `<select>` cannot be
   * corrected the way a text field can, and this app has no way to verify
   * a GL code against the entity's real bank account number). The account
   * number field therefore works exactly as before even when this list is
   * empty or fails to load — never a hard dependency for generating the
   * file.
   */
  const [accounts, setAccounts] = useState<PaymentSourceAccountOption[]>([]);
  const [accountsErrorReason, setAccountsErrorReason] = useState<
    'entitlement_required' | 'error' | null
  >(null);

  const canGenerate = can_generate_disbursement_file(run);
  const bankOptions: StyledSelectOption<DisbursementBankId>[] =
    DISBURSEMENT_BANKS.map((bank) => ({
      value: bank,
      label: t(BANK_NAME_KEY[bank]),
    }));
  const validationErrors = validate_disbursement_input({
    bank_id: bankId,
    debit_account_no: accountNo,
    value_date: valueDate,
  });

  useEffect(() => {
    if (!open) return;
    setAccounts([]);
    setAccountsErrorReason(null);
    void post_actions.list_payment_source_accounts().then((result) => {
      if (result.ok) {
        setAccounts(result.accounts);
      } else {
        setAccountsErrorReason(result.reason);
      }
    });
    // Only re-run when the dialog transitions open -- mirrors
    // `StatutoryRecordDialog`'s identical effect below.
  }, [open, post_actions]);

  function resetDialog(): void {
    setBankId(null);
    setAccountNo('');
    setValueDate('');
    setError(null);
  }

  /** Shared close path for the Cancel button AND Radix's own dismiss events (Escape/overlay click) — both MUST run `resetDialog()`. Calling `setOpen(false)` directly from the Cancel button (bypassing `onOpenChange`) previously skipped the reset entirely, since Radix's `onOpenChange` only fires for its own internal dismiss events, never for a parent-driven `open` prop change. */
  function handleClose(): void {
    setOpen(false);
    resetDialog();
  }

  async function handleSubmit(): Promise<void> {
    if (validationErrors.length > 0 || bankId === null) return;
    setError(null);
    setWarnings([]);
    setIsSubmitting(true);
    try {
      const result = await post_actions.get_salary_disbursement_file({
        pay_run_id: run.id,
        bank_id: bankId,
        debit_account_no: accountNo.trim(),
        value_date: valueDate,
      });
      if (result.ok) {
        // The file has already downloaded successfully at this point -- a
        // failure formatting the success message below (e.g. an
        // unrecognized `run.currency` triggering
        // `Intl.NumberFormat`'s `RangeError` inside `format_money`) must
        // NOT escape as an unhandled rejection from this handler, which
        // would leave the dialog open with the file downloaded but no
        // confirmation shown at all (adversarial-review finding, C8
        // second round, #22410). Isolated in its own try/catch so a
        // formatting bug degrades gracefully to a count-only success
        // message instead of crashing the whole submit handler.
        await triggerDownload(
          new Blob([result.content], { type: result.mime_type }),
          result.filename,
        );
        try {
          setSuccessMessage(
            t('pay_run.post_run.disbursement_success', {
              count: result.line_count,
              amount: format_money(result.total_cents, run.currency, locale),
            }),
          );
        } catch (err) {
          console.error(
            '[wagepro-web] formatting the disbursement success amount failed',
            err,
          );
          setSuccessMessage(
            t('pay_run.post_run.disbursement_success_count_only', {
              count: result.line_count,
            }),
          );
        }
        // Employees excluded for missing bank details / non-positive net pay
        // (server-computed, named per employee) MUST stay visible next to
        // the success banner -- a silently short-counted disbursement file
        // is a real payroll-correctness bug, not a cosmetic omission.
        setWarnings(result.warnings);
        setOpen(false);
        resetDialog();
      } else if (result.reason === 'entitlement_required') {
        setError(t('pay_run.post_run.disbursement_entitlement_required'));
      } else {
        // Never render `result.message` (the raw server BAD_REQUEST text)
        // directly -- classify it into a known failure class and render the
        // matching translated copy instead, same "log the real detail, show
        // a translated generic message" convention as `PayslipSection`'s
        // `handleDownload` above (adversarial-review finding, C8 second
        // round, #22410 -- this PR already fixed the identical leak class
        // for `statutory_filing_not_available` but left this path raw).
        console.error(
          '[wagepro-web] get salary disbursement file failed',
          result.message,
        );
        setError(
          t(
            DISBURSEMENT_ERROR_KEY[
              classify_disbursement_error_message(result.message)
            ],
          ),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="wp-post-run-section">
      <h3 className="font-semibold mb-2">{t('pay_run.generate_bank_file')}</h3>
      <Button
        variant="secondary"
        size="sm"
        disabled={!canGenerate}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('pay_run.post_run.disbursement_open_dialog')}
      </Button>
      {!canGenerate && (
        <p className="nv-help">
          {t('pay_run.post_run.disbursement_requires_approval')}
        </p>
      )}
      {successMessage && <Alert variant="success" title={successMessage} />}
      {warnings.length > 0 && (
        <Alert
          variant="warning"
          title={t('pay_run.post_run.disbursement_warnings_title')}
        >
          <ul className="list-disc pl-4">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      <AdaptiveDialog
        open={open}
        onClose={handleClose}
        title={t('pay_run.post_run.disbursement_dialog_title')}
        footer={
          <>
            <Button variant="ghost" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={validationErrors.length > 0 || isSubmitting}
              onClick={() => {
                void handleSubmit();
              }}
            >
              {isSubmitting
                ? t('pay_run.post_run.disbursement_generating')
                : t('pay_run.post_run.disbursement_submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 pt-2">
          <div className="flex flex-col gap-1">
            <label className="nv-label" htmlFor="disbursement-bank">
              {t('pay_run.post_run.disbursement_bank_label')}
            </label>
            <StyledSelect<DisbursementBankId | typeof NO_BANK_VALUE>
              id="disbursement-bank"
              value={bankId ?? NO_BANK_VALUE}
              options={bankOptions}
              placeholder={t('pay_run.post_run.disbursement_bank_placeholder')}
              onChange={(next) => {
                setBankId(next === NO_BANK_VALUE ? null : next);
              }}
            />
          </div>
          {accounts.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="nv-label" htmlFor="disbursement-account-picker">
                {t('pay_run.post_run.disbursement_account_picker_label')}
              </label>
              {/* `value=""` is deliberate and permanent: this picker never
                  holds a selection of its own. It is a one-shot convenience
                  that copies the chosen account's quick-fill value into the
                  editable `accountNo` Input below (see the `accounts` state
                  docblock above) — so the trigger always shows the
                  placeholder, and the account-number field stays the single
                  source of truth for `debit_account_no`. */}
              <StyledSelect
                id="disbursement-account-picker"
                value=""
                options={build_payment_source_quick_fill_options(accounts)}
                placeholder={t(
                  'pay_run.post_run.disbursement_account_select_placeholder',
                )}
                onChange={(next) => {
                  const selected = findPaymentSourceAccountByQuickFillValue(
                    accounts,
                    next,
                  );
                  if (selected) {
                    setAccountNo(resolvePaymentSourceQuickFillValue(selected));
                  }
                }}
              />
            </div>
          )}
          {accountsErrorReason === 'entitlement_required' && (
            <p className="nv-help" role="alert">
              {t('pay_run.post_run.disbursement_entitlement_required')}
            </p>
          )}
          {accountsErrorReason === 'error' && (
            <p className="nv-help" role="alert">
              {t('pay_run.post_run.disbursement_account_load_error')}
            </p>
          )}
          <div className="flex flex-col gap-1">
            <label className="nv-label" htmlFor="disbursement-account">
              {t('pay_run.post_run.disbursement_account_label')}
            </label>
            <Input
              id="disbursement-account"
              value={accountNo}
              inputMode="numeric"
              placeholder={t(
                'pay_run.post_run.disbursement_account_placeholder',
              )}
              onChange={(e) => {
                setAccountNo(e.target.value);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="nv-label" htmlFor="disbursement-value-date">
              {t('pay_run.post_run.disbursement_value_date_label')}
            </label>
            <DateInput
              id="disbursement-value-date"
              locale={locale}
              value={valueDate}
              onChange={(e) => {
                setValueDate(e.target.value);
              }}
            />
          </div>
          {error !== null && <Alert variant="danger" title={error} />}
        </div>
      </AdaptiveDialog>
    </div>
  );
}

function StatutoryRecordDialog({
  run,
  post_actions,
  row,
  open,
  onOpenChange,
  onRecorded,
}: {
  run: PayRun;
  post_actions: PayRunPostActionsController;
  row: StatutoryPaymentRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRecorded: () => void;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [accounts, setAccounts] = useState<BankAccountOption[]>([]);
  const [bankAccountId, setBankAccountId] = useState<string | null>(null);
  const [paidOn, setPaidOn] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * Set instead of `error` (never both) for the AAL2 step-up rejection
   * (#22829) — `assertMoneyWriteAllowed` rejects `recordStatutoryPayment` for
   * an AAL1 session the same way it already rejects `approvePayRun`
   * (#22821). Rendered with the dedicated 2FA guidance block (security
   * -settings link + "sign in again"), never the generic Alert, since a
   * blind retry can never clear this — mirrors `wizard_steps.tsx`'s
   * `ApprovalStep` treatment of `sign_off_error === 'step_up_required'`.
   */
  const [stepUpRequired, setStepUpRequired] = useState(false);
  const [accountsError, setAccountsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBankAccountId(null);
    setReference('');
    setError(null);
    setStepUpRequired(false);
    setAccountsError(false);
    // Clear the previous open's list immediately rather than letting it
    // flash stale while the new fetch is in flight.
    setAccounts([]);
    setPaidOn(new Date().toISOString().slice(0, 10));
    void post_actions.list_bank_accounts().then((result) => {
      if (result.ok) {
        setAccounts(result.accounts);
      } else {
        setAccountsError(true);
      }
    });
    // Only re-run when the dialog transitions open -- re-fetching on every
    // keystroke elsewhere in the component would be wasteful and pointless.
  }, [open, post_actions]);

  const isPaidOnValid = is_valid_iso_date(paidOn);

  async function handleSubmit(): Promise<void> {
    if (row === null || bankAccountId === null || !isPaidOnValid) return;
    // `record_statutory_payment` is `undefined` for a read-only grant (see
    // `wagepro_store.tsx`'s `PayRunPostActionsController` doc comment) --
    // the "Record" button that opens this dialog, AND this dialog's own
    // submit button (see its `disabled` prop below), are already hidden/
    // disabled in that case, so this is defence-in-depth, not the primary
    // gate -- but it still surfaces a real, translated message rather than
    // silently no-op-ing (adversarial-review finding).
    if (!post_actions.record_statutory_payment) {
      setError(t('pay_run.post_run.read_only_notice'));
      // Never both truthy (see `stepUpRequired`'s doc comment above) -- this
      // early-return path bypasses the submit-start reset below, so it must
      // clear `stepUpRequired` itself or a stale step-up guidance state from
      // an earlier submit attempt could linger and shadow this read-only
      // notice (adversarial-review finding).
      setStepUpRequired(false);
      return;
    }
    setError(null);
    setStepUpRequired(false);
    setIsSubmitting(true);
    try {
      const result = await post_actions.record_statutory_payment({
        pay_run_id: run.id,
        payment_type: row.payment_type,
        amount_cents: row.computed_due_cents,
        paid_on: paidOn,
        reference: reference.trim() === '' ? undefined : reference.trim(),
        bank_account_id: bankAccountId,
      });
      if (result.ok) {
        onRecorded();
        onOpenChange(false);
      } else if (result.reason === 'step_up_required') {
        // AAL2 step-up rejection (#22829) -- rendered with dedicated 2FA
        // guidance below, never the generic Alert, since a blind retry can
        // never clear this (see `stepUpRequired`'s doc comment above).
        setStepUpRequired(true);
      } else if (result.reason === 'entitlement_required') {
        // Narrow, positive classification -- NEVER render the internal
        // `'entitlement_required'` token itself as alert copy (adversarial
        // -review finding: an earlier version passed `result.reason`
        // straight into `setError`, which for this branch is the literal
        // string `'entitlement_required'`, untranslated, shown verbatim).
        setError(t('pay_run.post_run.statutory_entitlement_required'));
      } else {
        // Never render `result.message` (the raw server error text)
        // directly -- classify it into a known failure class and render the
        // matching translated copy instead, same convention as
        // `DisbursementDialog.handleSubmit`'s `DISBURSEMENT_ERROR_KEY`
        // lookup above (adversarial-review finding, C9 second round,
        // #22415: this branch previously rendered `result.message` verbatim
        // to every locale, including si/ta/id/fr users).
        console.error(
          '[wagepro-web] record statutory payment failed',
          result.message,
        );
        setError(
          t(
            STATUTORY_ERROR_KEY[
              classify_statutory_error_message(result.message)
            ],
          ),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AdaptiveDialog
      open={open}
      onClose={() => {
        onOpenChange(false);
      }}
      title={t('pay_run.post_run.statutory_dialog_title')}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={
              bankAccountId === null ||
              !isPaidOnValid ||
              isSubmitting ||
              !post_actions.record_statutory_payment
            }
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isSubmitting
              ? t('pay_run.post_run.statutory_recording')
              : t('pay_run.post_run.statutory_record_submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 pt-2">
        <div className="glass-soft rounded-md px-3 py-2 text-[13px] flex justify-between font-medium">
          <span className="text-[color:var(--ink-muted)]">
            {t('pay_run.post_run.statutory_amount_due_label')}
          </span>
          <span className="num">
            {row
              ? format_money(row.computed_due_cents, run.currency, locale)
              : ''}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <label className="nv-label" htmlFor="statutory-paid-on">
            {t('pay_run.post_run.statutory_date_label')}
          </label>
          <DateInput
            id="statutory-paid-on"
            locale={locale}
            value={paidOn}
            onChange={(e) => {
              setPaidOn(e.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="nv-label" htmlFor="statutory-reference">
            {t('pay_run.post_run.statutory_reference_label')}
          </label>
          <Input
            id="statutory-reference"
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="nv-label" htmlFor="statutory-bank-account">
            {t('pay_run.post_run.statutory_bank_account_label')}
          </label>
          {/* Disabled while the list is empty -- during the fetch, after a
              failed fetch (the `role="alert"` copy below says why), or when
              the entity simply has no eligible account -- so the trigger
              never opens an empty listbox (#24385 review, R2). */}
          <StyledSelect
            id="statutory-bank-account"
            value={bankAccountId ?? ''}
            options={build_bank_account_options(accounts)}
            placeholder={t(
              'pay_run.post_run.statutory_bank_account_placeholder',
            )}
            disabled={accounts.length === 0}
            onChange={(next) => {
              setBankAccountId(next);
            }}
          />
          {accountsError ? (
            <p className="nv-help" role="alert">
              {t('pay_run.post_run.statutory_bank_account_load_error')}
            </p>
          ) : (
            bankAccountId === null && (
              <p className="nv-help">
                {t('pay_run.post_run.statutory_bank_account_required')}
              </p>
            )
          )}
        </div>
        {stepUpRequired ? (
          <div role="alert">
            <p className="nv-help is-error mt-0">
              {t('pay_run.post_run.statutory_record_step_up_required')}
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                variant="secondary"
                size="xs"
                onClick={() => {
                  restartSignIn();
                }}
              >
                {t('auth.step_up.sign_in_again_cta')}
              </Button>
            </div>
          </div>
        ) : (
          error !== null && <Alert variant="danger" title={error} />
        )}
      </div>
    </AdaptiveDialog>
  );
}

export function StatutorySection({
  run,
  post_actions,
}: {
  run: PayRun;
  post_actions: PayRunPostActionsController;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [rows, setRows] = useState<StatutoryPaymentRow[] | null>(null);
  const [loadErrorReason, setLoadErrorReason] =
    useState<PayrollActionFailureReason | null>(null);
  const [recordingType, setRecordingType] =
    useState<StatutoryPaymentType | null>(null);
  const [voidingPayment, setVoidingPayment] =
    useState<StatutoryPaymentRecord | null>(null);
  const [isVoiding, setIsVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  /** Same "set instead of voidError, never both" contract as `StatutoryRecordDialog`'s `stepUpRequired` above -- see that state's doc comment (#22829). */
  const [voidStepUpRequired, setVoidStepUpRequired] = useState(false);

  const refresh = useCallback((): void => {
    void post_actions.list_statutory_payments(run.id).then((result) => {
      if (result.ok) {
        setRows(
          build_statutory_payment_rows(
            result.computed_due_cents,
            result.payments,
          ),
        );
        setLoadErrorReason(null);
      } else {
        // Forwarded verbatim from `use_pay_run_post_actions.ts` -- distinct
        // `entitlement_required` copy below, never a generic message for
        // that specific, actionable cause (adversarial-review finding: an
        // earlier version collapsed this to a bare boolean, which could
        // never show "buy the payroll add-on" here).
        setLoadErrorReason(result.reason);
      }
    });
  }, [post_actions, run.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canRecord = can_record_statutory_payment(run);
  const recordingRow =
    rows?.find((r) => r.payment_type === recordingType) ?? null;

  async function handleVoidConfirm(): Promise<void> {
    if (voidingPayment === null) return;
    // `void_statutory_payment` is `undefined` for a read-only grant (see
    // `wagepro_store.tsx`'s `PayRunPostActionsController` doc comment) --
    // the "Void" button that opens this confirm is already hidden in that
    // case, so this is defence-in-depth, not the primary gate -- but it
    // still surfaces a real, translated message rather than silently
    // no-op-ing (adversarial-review finding).
    if (!post_actions.void_statutory_payment) {
      setVoidError(t('pay_run.post_run.read_only_notice'));
      // Never both truthy -- same "clear the other state on this early
      // -return path too" fix as `StatutoryRecordDialog.handleSubmit` above
      // (adversarial-review finding).
      setVoidStepUpRequired(false);
      return;
    }
    setVoidError(null);
    setVoidStepUpRequired(false);
    setIsVoiding(true);
    try {
      const result = await post_actions.void_statutory_payment({
        pay_run_id: run.id,
        payment_id: voidingPayment.id,
      });
      if (result.ok) {
        setVoidingPayment(null);
        refresh();
      } else if (result.reason === 'step_up_required') {
        // AAL2 step-up rejection (#22829) -- same dedicated 2FA guidance
        // treatment as `StatutoryRecordDialog.handleSubmit` above.
        setVoidStepUpRequired(true);
      } else if (result.reason === 'entitlement_required') {
        // Never render the internal `'entitlement_required'` token itself
        // as alert copy (same finding as `StatutoryRecordDialog.handleSubmit`
        // above).
        setVoidError(t('pay_run.post_run.statutory_entitlement_required'));
      } else {
        // Never render `result.message` verbatim -- same classification
        // convention as `StatutoryRecordDialog.handleSubmit` above
        // (adversarial-review finding, C9 second round, #22415).
        console.error(
          '[wagepro-web] void statutory payment failed',
          result.message,
        );
        setVoidError(
          t(
            STATUTORY_ERROR_KEY[
              classify_statutory_error_message(result.message)
            ],
          ),
        );
      }
    } finally {
      setIsVoiding(false);
    }
  }

  return (
    <div className="wp-post-run-section">
      <h3 className="font-semibold mb-2">
        {t('pay_run.post_run.statutory_title')}
      </h3>
      {!canRecord && (
        <p className="nv-help mb-2">
          {t('pay_run.post_run.statutory_requires_approval')}
        </p>
      )}
      {/* The read-only-grant notice itself is rendered ONCE, at the
          `PostRunStep` composition root (`wizard_steps.tsx`) -- not
          repeated here (adversarial-review finding: an earlier version
          duplicated this exact paragraph in both this section and
          `FilingSection`). */}
      {loadErrorReason === 'entitlement_required' && (
        <Alert
          variant="danger"
          title={t('pay_run.post_run.statutory_entitlement_required')}
        />
      )}
      {loadErrorReason === 'error' && (
        <Alert
          variant="danger"
          title={t('pay_run.post_run.statutory_load_error')}
        />
      )}
      {rows && (
        <GlassTable>
          <table className="nv-table">
            <thead>
              <tr>
                <th scope="col">
                  {t('pay_run.post_run.statutory_column_type')}
                </th>
                <th scope="col" className="text-end">
                  {t('pay_run.post_run.statutory_column_due')}
                </th>
                <th scope="col">
                  {t('pay_run.post_run.statutory_column_status')}
                </th>
                <th scope="col">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.payment_type}>
                  <td>{t(STATUTORY_TYPE_KEY[row.payment_type])}</td>
                  <td className="text-end num">
                    {format_money(row.computed_due_cents, run.currency, locale)}
                  </td>
                  <td>
                    <StatusPill
                      status={row.recorded ? 'approved' : 'pending'}
                      label={
                        // `t()` is called on each branch rather than wrapping
                        // a ternary, so both keys are visible to the static
                        // i18n scanners (`i18n:lint` / `i18n:no-orphans`),
                        // which only match a literal directly inside `t(...)`.
                        row.recorded
                          ? t('pay_run.post_run.statutory_status_recorded')
                          : t('pay_run.post_run.statutory_status_due')
                      }
                    />
                  </td>
                  <td className="text-end">
                    {row.recorded
                      ? // WRITE action -- `void_statutory_payment` is
                        // `undefined` entirely for a read-only grant, hiding
                        // this button rather than letting it 403 on click
                        // (see `wagepro_store.tsx`'s `PayRunPostActionsController`
                        // doc comment).
                        post_actions.void_statutory_payment && (
                          <Button
                            variant="ghost-danger"
                            size="xs"
                            onClick={() => {
                              if (row.recorded) setVoidingPayment(row.recorded);
                            }}
                          >
                            {t('pay_run.post_run.statutory_void_action')}
                          </Button>
                        )
                      : // WRITE action -- same read-only-grant hiding as above.
                        post_actions.record_statutory_payment && (
                          <Button
                            variant="secondary"
                            size="xs"
                            // A zero computed-due amount (e.g. an employer
                            // under the PAYE threshold this period) can
                            // never actually be recorded --
                            // `recordStatutoryPayment` requires
                            // `amount_cents >= 1` (payroll.ts) and would
                            // reject it with a raw validation error.
                            // Disable rather than let the button open a
                            // dialog that can only fail.
                            disabled={!canRecord || row.computed_due_cents <= 0}
                            onClick={() => {
                              setRecordingType(row.payment_type);
                            }}
                          >
                            {t('pay_run.post_run.statutory_record_action')}
                          </Button>
                        )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassTable>
      )}

      <StatutoryRecordDialog
        run={run}
        post_actions={post_actions}
        row={recordingRow}
        open={recordingType !== null}
        onOpenChange={(open) => {
          if (!open) setRecordingType(null);
        }}
        onRecorded={refresh}
      />

      {voidingPayment !== null && (
        <VoidStatutoryConfirm
          open
          loading={isVoiding}
          error={voidError}
          stepUpRequired={voidStepUpRequired}
          canVoid={post_actions.void_statutory_payment !== undefined}
          onOpenChange={(open) => {
            if (!open) {
              setVoidingPayment(null);
              setVoidError(null);
              setVoidStepUpRequired(false);
            }
          }}
          onConfirm={() => {
            void handleVoidConfirm();
          }}
        />
      )}
    </div>
  );
}

function VoidStatutoryConfirm({
  open,
  loading,
  error,
  stepUpRequired,
  canVoid,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  /** Rendered INSIDE the dialog -- a void failure must stay visible while the confirm dialog is still open, not behind its own overlay (adversarial review finding). */
  error: string | null;
  /**
   * The AAL2 step-up rejection (#22829) -- set instead of `error` (never
   * both), rendered with the dedicated 2FA guidance block below rather than
   * the generic Alert, since a blind retry can never clear this. Same
   * contract as `StatutoryRecordDialog`'s `stepUpRequired` state.
   */
  stepUpRequired: boolean;
  /** `post_actions.void_statutory_payment !== undefined` -- disables the confirm button as a second, defence-in-depth gate alongside `handleVoidConfirm`'s own guard (the button that opens this dialog is already hidden for a read-only grant, so this is belt-and-braces, not the primary gate). */
  canVoid: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {t('pay_run.post_run.statutory_void_confirm_title')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-[color:var(--ink-muted)]">
          {t('pay_run.post_run.statutory_void_confirm_description')}
        </p>
        {stepUpRequired ? (
          <div className="pt-2" role="alert">
            <p className="nv-help is-error mt-0">
              {t('pay_run.post_run.statutory_void_step_up_required')}
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                variant="secondary"
                size="xs"
                onClick={() => {
                  restartSignIn();
                }}
              >
                {t('auth.step_up.sign_in_again_cta')}
              </Button>
            </div>
          </div>
        ) : (
          error !== null && (
            <div className="pt-2">
              <Alert variant="danger" title={error} />
            </div>
          )
        )}
        <div className="flex justify-end gap-2 pt-3">
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            disabled={loading || !canVoid}
            onClick={onConfirm}
          >
            {t('pay_run.post_run.statutory_void_button')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FILING_STATUS_KEY: Record<string, string> = {
  upcoming: 'pay_run.post_run.filing_status_upcoming',
  submitted: 'pay_run.post_run.filing_status_submitted',
  overdue: 'pay_run.post_run.filing_status_overdue',
  waived: 'pay_run.post_run.filing_status_waived',
};

/**
 * Translated copy for `generate_upcoming_filings`/`mark_filed`'s
 * discriminated `FilingCalendarFailureReason` -- NEVER the raw server/network
 * message (adversarial-review finding: an earlier version rendered
 * `result.reason` as a bare, untranslated English string, which for a
 * FORBIDDEN before C6, #22325 merges would have been the deny-by-default
 * agent-token gate's own English sentence, shown verbatim to si/ta/id users).
 */
const FILING_FAILURE_KEY: Record<FilingCalendarFailureReason, string> = {
  permission_required: 'pay_run.post_run.filing_permission_required',
  jurisdiction_unsupported: 'pay_run.post_run.filing_jurisdiction_unsupported',
  error: 'pay_run.post_run.filing_action_error',
};

/**
 * Same classified reasons as {@link FILING_FAILURE_KEY}, but for the
 * `list_filing_obligations` LOAD path specifically -- keeps the more
 * fitting "Could not load the filing calendar" copy for the generic
 * `'error'` case (rather than `filing_action_error`'s "Could not complete
 * this action", written for the generate/mark-filed WRITE actions) while
 * still surfacing the specific `permission_required`/`jurisdiction_unsupported`
 * copy when the load itself is classified as one of those (adversarial
 * -review finding, C9 second round, #22415: `list_filing_obligations`
 * previously discarded its own classified `reason` entirely, collapsing
 * every load failure to this one generic message -- see
 * `use_pay_run_post_actions.ts`'s `list_filing_obligations` for the fix that
 * stopped discarding it).
 */
const FILING_LOAD_FAILURE_KEY: Record<FilingCalendarFailureReason, string> = {
  permission_required: 'pay_run.post_run.filing_permission_required',
  jurisdiction_unsupported: 'pay_run.post_run.filing_jurisdiction_unsupported',
  error: 'pay_run.post_run.filing_load_error',
};

/**
 * `generateErrorReason`/`markFiledErrorReason` need one more state than the
 * server ever actually returns: `'read_only'`, set ONLY by this component's
 * own read-only-grant fallback guards below (never returned by
 * `generate_upcoming_filings`/`mark_filed` themselves) -- a defence-in-depth
 * branch for the (should-be-unreachable) case where the write method is
 * `undefined` but its button rendered anyway. Previously these fallbacks
 * reused `'permission_required'` ("Ask an administrator for access"), which
 * is the wrong message for a read-only-grant user who DOES have access, just
 * not write access (adversarial-review finding, C9 second round, #22415) --
 * `'read_only'` renders the same `read_only_notice` copy `StatutorySection`'s
 * identical fallback guards already use.
 */
type FilingActionErrorReason = FilingCalendarFailureReason | 'read_only';

export function FilingSection({
  post_actions,
}: {
  post_actions: PayRunPostActionsController;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [obligations, setObligations] = useState<
    FilingObligationSummary[] | null
  >(null);
  const [loadErrorReason, setLoadErrorReason] =
    useState<FilingCalendarFailureReason | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateErrorReason, setGenerateErrorReason] =
    useState<FilingActionErrorReason | null>(null);
  const [markingKey, setMarkingKey] = useState<string | null>(null);
  const [markFiledErrorReason, setMarkFiledErrorReason] =
    useState<FilingActionErrorReason | null>(null);
  // #23316 — same discriminated failure state as markFiled above; a separate
  // key so a click on either button disables only its own row-cell control
  // rather than freezing the whole row.
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [markSubmittedErrorReason, setMarkSubmittedErrorReason] =
    useState<FilingActionErrorReason | null>(null);

  const refresh = useCallback((): void => {
    void post_actions.list_filing_obligations().then((result) => {
      if (result.ok) {
        setObligations(result.obligations);
        setLoadErrorReason(null);
      } else {
        // Forward the classified `reason` rather than collapsing to a bare
        // boolean -- see `FILING_LOAD_FAILURE_KEY`'s doc comment
        // (adversarial-review finding, C9 second round, #22415).
        setLoadErrorReason(result.reason);
      }
    });
  }, [post_actions]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleGenerate(): Promise<void> {
    // `generate_upcoming_filings` is `undefined` for a read-only grant (see
    // `wagepro_store.tsx`'s `PayRunPostActionsController` doc comment) --
    // the button below is already hidden in that case, so this is
    // defence-in-depth, not the primary gate -- but it still surfaces a
    // real, translated message rather than silently no-op-ing
    // (adversarial-review finding).
    if (!post_actions.generate_upcoming_filings) {
      setGenerateErrorReason('read_only');
      return;
    }
    setGenerateErrorReason(null);
    setIsGenerating(true);
    try {
      const result = await post_actions.generate_upcoming_filings();
      if (result.ok) {
        refresh();
      } else {
        // Reachable for a missing `reports_access` permission, a non-LK
        // entity, or (pre-C6, #22325) the OAuth allowlist gap -- all real,
        // if uncommon, configurations; a silent no-op here left the button
        // looking broken with zero signal (adversarial review finding).
        // `result.reason` is a discriminated, TRANSLATED reason, never the
        // raw server/network message (a second, later adversarial-review
        // finding: an earlier version rendered that raw message verbatim,
        // untranslated, to si/ta/id users).
        setGenerateErrorReason(result.reason);
      }
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleMarkFiled(reportId: string): Promise<void> {
    const parsed = parse_filing_report_id(reportId);
    if (!parsed) return;
    // `mark_filed` is `undefined` for a read-only grant -- the button below
    // is already hidden in that case, so this is defence-in-depth, not the
    // primary gate.
    if (!post_actions.mark_filed) {
      setMarkFiledErrorReason('read_only');
      return;
    }
    setMarkFiledErrorReason(null);
    setMarkingKey(reportId);
    try {
      const result = await post_actions.mark_filed(parsed);
      if (result.ok) {
        refresh();
      } else {
        setMarkFiledErrorReason(result.reason);
      }
    } finally {
      setMarkingKey(null);
    }
  }

  async function handleMarkSubmitted(obligationId: string): Promise<void> {
    // `mark_submitted` is `undefined` for a read-only grant -- the button
    // below is already hidden in that case, so this is defence-in-depth,
    // not the primary gate. Uses the same discriminated `read_only`
    // fallback the sibling `handleMarkFiled` uses (not
    // `'permission_required'`, which would render the wrong "Ask an
    // administrator" copy for a user who has access but not write access —
    // adversarial-review finding baked into `FilingActionErrorReason`).
    if (!post_actions.mark_submitted) {
      setMarkSubmittedErrorReason('read_only');
      return;
    }
    setMarkSubmittedErrorReason(null);
    setSubmittingKey(obligationId);
    try {
      const result = await post_actions.mark_submitted({
        obligation_id: obligationId,
      });
      if (result.ok) {
        refresh();
      } else {
        setMarkSubmittedErrorReason(result.reason);
      }
    } finally {
      setSubmittingKey(null);
    }
  }

  return (
    <div className="wp-post-run-section">
      <h3 className="font-semibold mb-2">{t('pay_run.mark_filings_due')}</h3>
      {/* WRITE action -- `generate_upcoming_filings` is `undefined` entirely
          for a read-only grant, hiding this button rather than letting it
          403 on click (see `wagepro_store.tsx`'s `PayRunPostActionsController`
          doc comment). */}
      {post_actions.generate_upcoming_filings && (
        <Button
          variant="secondary"
          size="sm"
          disabled={isGenerating}
          onClick={() => {
            void handleGenerate();
          }}
        >
          {isGenerating
            ? t('pay_run.post_run.filing_generating')
            : t('pay_run.post_run.filing_generate_button')}
        </Button>
      )}
      {/* The read-only-grant notice itself is rendered ONCE, at the
          `PostRunStep` composition root (`wizard_steps.tsx`) -- see
          `StatutorySection`'s identical comment above. */}

      {generateErrorReason !== null && (
        <Alert
          variant="danger"
          title={
            generateErrorReason === 'read_only'
              ? t('pay_run.post_run.read_only_notice')
              : t(FILING_FAILURE_KEY[generateErrorReason])
          }
        />
      )}
      {markFiledErrorReason !== null && (
        <Alert
          variant="danger"
          title={
            markFiledErrorReason === 'read_only'
              ? t('pay_run.post_run.read_only_notice')
              : t(FILING_FAILURE_KEY[markFiledErrorReason])
          }
        />
      )}
      {markSubmittedErrorReason !== null && (
        <Alert
          variant="danger"
          title={
            markSubmittedErrorReason === 'read_only'
              ? t('pay_run.post_run.read_only_notice')
              : t(FILING_FAILURE_KEY[markSubmittedErrorReason])
          }
        />
      )}

      {loadErrorReason !== null && (
        <Alert
          variant="danger"
          title={t(FILING_LOAD_FAILURE_KEY[loadErrorReason])}
        />
      )}

      {obligations && obligations.length === 0 && loadErrorReason === null && (
        <p className="nv-help mt-2">{t('pay_run.post_run.filing_empty')}</p>
      )}

      {obligations && obligations.length > 0 && (
        <GlassTable className="mt-2">
          <table className="nv-table">
            <thead>
              <tr>
                <th scope="col">
                  {t('pay_run.post_run.filing_column_obligation')}
                </th>
                <th scope="col">{t('pay_run.post_run.filing_column_due')}</th>
                <th scope="col">
                  {t('pay_run.post_run.filing_column_status')}
                </th>
                <th scope="col">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {obligations.map((obligation) => {
                const parsed = parse_filing_report_id(obligation.report_id);
                const statusKey =
                  FILING_STATUS_KEY[obligation.status] ??
                  'pay_run.post_run.filing_status_unknown';
                return (
                  <tr key={obligation.obligation_id}>
                    <td className="mono break-all">{obligation.report_id}</td>
                    <td>{format_date(obligation.due_date, locale)}</td>
                    <td>
                      <StatusPill
                        status={
                          obligation.status === 'submitted'
                            ? 'approved'
                            : 'pending'
                        }
                        label={t(statusKey)}
                      />
                    </td>
                    <td className="text-end">
                      <div className="flex justify-end gap-2">
                        {obligation.status !== 'submitted' &&
                          parsed &&
                          // WRITE action -- same read-only-grant hiding as
                          // the "Generate" button above.
                          post_actions.mark_filed && (
                            <Button
                              variant="secondary"
                              size="xs"
                              disabled={markingKey === obligation.report_id}
                              onClick={() => {
                                void handleMarkFiled(obligation.report_id);
                              }}
                            >
                              {t('pay_run.post_run.filing_mark_filed')}
                            </Button>
                          )}
                        {/* #23316 — legacy sibling of "Mark filed" keyed by
                            the opaque `obligation_id` ULID rather than
                            `obligation:period_label`. Rendered even when
                            `parsed` is null (a row whose `report_id` is not
                            the canonical `obligation:period_label` shape),
                            so the ULID-keyed path stays reachable exactly
                            where the canonical path is not. Same
                            read-only-grant hiding as every other WRITE
                            button in this section. */}
                        {obligation.status !== 'submitted' &&
                          post_actions.mark_submitted && (
                            <Button
                              variant="secondary"
                              size="xs"
                              disabled={
                                submittingKey === obligation.obligation_id
                              }
                              onClick={() => {
                                void handleMarkSubmitted(
                                  obligation.obligation_id,
                                );
                              }}
                            >
                              {t('pay_run.post_run.filing_mark_submitted')}
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </GlassTable>
      )}
    </div>
  );
}
